export type LocaleDefinition = {
  code: string
  /** BCP-47 language tag for i18next / document.lang */
  languageTag: string
  /** Display name in English */
  name: string
  /** Native endonym shown in the language picker */
  nativeName: string
  /** ISO 3166-1 alpha-2 country code for flag-icons (fi fi-xx) */
  flagCountry: string
  rtl?: boolean
}

/** Global languages (Hindi only — no other Indian regional languages) */
export const LOCALE_DEFINITIONS = [
  { code: 'en', languageTag: 'en', name: 'English', nativeName: 'English', flagCountry: 'us' },
  { code: 'zh', languageTag: 'zh', name: 'Chinese', nativeName: '中文', flagCountry: 'cn' },
  { code: 'hi', languageTag: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flagCountry: 'in' },
  { code: 'es', languageTag: 'es', name: 'Spanish', nativeName: 'Español', flagCountry: 'es' },
  { code: 'ar', languageTag: 'ar', name: 'Arabic', nativeName: 'العربية', flagCountry: 'sa', rtl: true },
  { code: 'fr', languageTag: 'fr', name: 'French', nativeName: 'Français', flagCountry: 'fr' },
  { code: 'pt', languageTag: 'pt', name: 'Portuguese', nativeName: 'Português', flagCountry: 'pt' },
  { code: 'ru', languageTag: 'ru', name: 'Russian', nativeName: 'Русский', flagCountry: 'ru' },
  { code: 'ur', languageTag: 'ur', name: 'Urdu', nativeName: 'اردو', flagCountry: 'pk', rtl: true },
  { code: 'id', languageTag: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flagCountry: 'id' },
  { code: 'de', languageTag: 'de', name: 'German', nativeName: 'Deutsch', flagCountry: 'de' },
  { code: 'ja', languageTag: 'ja', name: 'Japanese', nativeName: '日本語', flagCountry: 'jp' },
  { code: 'sw', languageTag: 'sw', name: 'Swahili', nativeName: 'Kiswahili', flagCountry: 'ke' },
  { code: 'tr', languageTag: 'tr', name: 'Turkish', nativeName: 'Türkçe', flagCountry: 'tr' },
  { code: 'vi', languageTag: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flagCountry: 'vn' },
  { code: 'ko', languageTag: 'ko', name: 'Korean', nativeName: '한국어', flagCountry: 'kr' },
  { code: 'it', languageTag: 'it', name: 'Italian', nativeName: 'Italiano', flagCountry: 'it' },
  { code: 'th', languageTag: 'th', name: 'Thai', nativeName: 'ไทย', flagCountry: 'th' },
  { code: 'pl', languageTag: 'pl', name: 'Polish', nativeName: 'Polski', flagCountry: 'pl' },
  { code: 'uk', languageTag: 'uk', name: 'Ukrainian', nativeName: 'Українська', flagCountry: 'ua' },
  { code: 'ms', languageTag: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', flagCountry: 'my' },
  { code: 'he', languageTag: 'he', name: 'Hebrew', nativeName: 'עברית', flagCountry: 'il', rtl: true },
  { code: 'nl', languageTag: 'nl', name: 'Dutch', nativeName: 'Nederlands', flagCountry: 'nl' },
  { code: 'ro', languageTag: 'ro', name: 'Romanian', nativeName: 'Română', flagCountry: 'ro' },
  { code: 'hu', languageTag: 'hu', name: 'Hungarian', nativeName: 'Magyar', flagCountry: 'hu' },
  { code: 'el', languageTag: 'el', name: 'Greek', nativeName: 'Ελληνικά', flagCountry: 'gr' },
  { code: 'cs', languageTag: 'cs', name: 'Czech', nativeName: 'Čeština', flagCountry: 'cz' },
  { code: 'sv', languageTag: 'sv', name: 'Swedish', nativeName: 'Svenska', flagCountry: 'se' },
  { code: 'fil', languageTag: 'fil', name: 'Filipino', nativeName: 'Filipino', flagCountry: 'ph' },
] as const satisfies readonly LocaleDefinition[]

export const SUPPORTED_LOCALES = LOCALE_DEFINITIONS.map((l) => l.code)

export type AppLocale = (typeof LOCALE_DEFINITIONS)[number]['code']

export const RTL_LOCALES = new Set<AppLocale>(['ar', 'he', 'ur'])

export const LOCALE_BY_CODE = Object.fromEntries(
  LOCALE_DEFINITIONS.map((l) => [l.code, l])
) as Record<AppLocale, LocaleDefinition>

export function isRtlLocale(locale: string): boolean {
  return RTL_LOCALES.has(locale as AppLocale)
}

export function isSupportedLocale(locale: string | null | undefined): locale is AppLocale {
  return typeof locale === 'string' && locale in LOCALE_BY_CODE
}
